import React, { useState } from 'react';
import { Shield, X, ArrowDownToLine, MousePointer2, Coins, Calendar } from 'lucide-react';
import { ConfigTab } from './ConfigTab';
import { LimitsTab } from './LimitsTab';
import { PurchasesTab } from './PurchasesTab';
import { FortuneWheelTab } from './FortuneWheelTab';
import { DailyLoginTab } from './DailyLoginTab';

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentWallet: string;
  contractAddress?: string;
  mainWallet?: string;
}

export function AdminPanel({ 
  isOpen, 
  onClose, 
  currentWallet,
  contractAddress = '',
  mainWallet = ''
}: AdminPanelProps) {
  const [activeTab, setActiveTab] = useState<'config'|'limits'|'purchases'|'fortune'|'daily'>('config');

  if (!isOpen) return null;

  const tabs = [
    { id: 'config', label: 'Configuration', icon: <Shield size={20} /> },
    { id: 'limits', label: 'Limits', icon: <ArrowDownToLine size={20} /> },
    { id: 'purchases', label: 'Purchases', icon: <MousePointer2 size={20} /> },
    { id: 'fortune', label: 'Roue de la Fortune', icon: <Coins size={20} /> },
    { id: 'daily', label: 'Bonus Quotidiens', icon: <Calendar size={20} /> }
  ];

  const inputClasses = "w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white";

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-white">
              <Shield className="text-blue-500" />
              Admin Configuration
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="mt-4 p-4 bg-blue-900/20 rounded-lg border border-blue-500/20">
            <p className="text-blue-300 text-sm">
              Connected wallet: <span className="font-mono">{currentWallet}</span>
            </p>
          </div>

          <div className="flex space-x-2 mt-4 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'config'|'limits'|'purchases'|'fortune'|'daily')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'config' && <ConfigTab inputClasses={inputClasses} currentWallet={currentWallet} />}
          {activeTab === 'limits' && <LimitsTab inputClasses={inputClasses} currentWallet={currentWallet} />}
          {activeTab === 'purchases' && <PurchasesTab inputClasses={inputClasses} currentWallet={currentWallet} />}
          {activeTab === 'fortune' && <FortuneWheelTab inputClasses={inputClasses} currentWallet={currentWallet} />}
          {activeTab === 'daily' && <DailyLoginTab inputClasses={inputClasses} currentWallet={currentWallet} />}
        </div>
      </div>
    </div>
  );
}